import apiClient from "@/api/client";
import type { EmployeeTaskStats, SubTask, SubTaskAttachment, SubTaskFilters } from "@/features/tasks/types";

const wrap = <T>(res: { data: { data: T } }) => res.data.data;

export const subtasksApi = {
  list: (taskId: string) =>
    apiClient.get<{ data: SubTask[] }>(`/tasks/${taskId}/subtasks/`).then(wrap),

  listAll: (filters?: SubTaskFilters) => {
    const params = new URLSearchParams();
    if (filters) {
      const { assigned_to, project, client, include_archived, ...rest } = filters;
      Object.entries(rest).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== "") params.append(k, String(v));
      });
      if (assigned_to?.length) assigned_to.forEach((id) => params.append("assigned_to", id));
      if (project?.length) project.forEach((id) => params.append("project", id));
      if (client?.length) client.forEach((id) => params.append("client", id));
      if (include_archived) params.append("include_archived", "true");
    }
    return apiClient.get<{ data: SubTask[] }>("/subtasks/", { params }).then(wrap);
  },

  my: () =>
    apiClient.get<{ data: SubTask[] }>("/subtasks/my/").then(wrap),

  create: (taskId: string, data: { title: string; parent?: string | null; due_date?: string | null; assignee_ids?: string[]; priority?: string; status?: string; }) =>
    apiClient.post<{ data: SubTask }>(`/tasks/${taskId}/subtasks/`, data).then(wrap),

  update: (taskId: string, id: string, data: Partial<SubTask> & { assignee_ids?: string[] }) =>
    apiClient.patch<{ data: SubTask }>(`/tasks/${taskId}/subtasks/${id}/`, data).then(wrap),

  delete: (taskId: string, id: string) =>
    apiClient.delete(`/tasks/${taskId}/subtasks/${id}/`),

  toggle: (taskId: string, id: string) =>
    apiClient.patch<{ data: SubTask }>(`/tasks/${taskId}/subtasks/${id}/toggle/`, {}).then(wrap),

  move: (taskId: string, subtaskId: string, newTaskId: string) =>
    apiClient.patch<{ data: SubTask }>(`/tasks/${taskId}/subtasks/${subtaskId}/move/`, { task_id: newTaskId }).then(wrap),

  reparent: (taskId: string, subtaskId: string, data: { parent_id?: string | null; task_id?: string }) =>
    apiClient.patch<{ data: SubTask }>(`/tasks/${taskId}/subtasks/${subtaskId}/reparent/`, data).then(wrap),

  reorder: (taskId: string, ids: string[]) =>
    apiClient.post<{ data: SubTask[] }>(`/tasks/${taskId}/subtasks/reorder/`, { ids }).then(wrap),

  listAttachments: (taskId: string, subtaskId: string) =>
    apiClient.get<{ data: SubTaskAttachment[] }>(`/tasks/${taskId}/subtasks/${subtaskId}/attachments/`).then(wrap),

  uploadAttachment: (taskId: string, subtaskId: string, file: File) => {
    const form = new FormData();
    form.append("file", file);
    return apiClient.post<{ data: SubTaskAttachment }>(`/tasks/${taskId}/subtasks/${subtaskId}/attachments/`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then(wrap);
  },

  deleteAttachment: (taskId: string, subtaskId: string, attachmentId: string) =>
    apiClient.delete(`/tasks/${taskId}/subtasks/${subtaskId}/attachments/${attachmentId}/`),

  uploadCommentAttachment: (taskId: string, subtaskId: string, commentId: string, file: File) => {
    const form = new FormData();
    form.append("file", file);
    return apiClient.post(`/tasks/${taskId}/subtasks/${subtaskId}/comments/${commentId}/attachments/`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },

  deleteCommentAttachment: (taskId: string, subtaskId: string, commentId: string, attachmentId: string) =>
    apiClient.delete(`/tasks/${taskId}/subtasks/${subtaskId}/comments/${commentId}/attachments/${attachmentId}/`),

  watch: (taskId: string, subtaskId: string) =>
    apiClient.post(`/tasks/${taskId}/subtasks/${subtaskId}/watch/`, {}).then((r: { data: { data: { is_watching: boolean } } }) => r.data.data),

  unwatch: (taskId: string, subtaskId: string) =>
    apiClient.post(`/tasks/${taskId}/subtasks/${subtaskId}/unwatch/`, {}).then((r: { data: { data: { is_watching: boolean } } }) => r.data.data),

  employeeStats: () =>
    apiClient.get<{ data: EmployeeTaskStats[] }>("/subtasks/employee-stats/").then(wrap),
};
